import { useEffect } from 'react';
import { Brain, RefreshCw, TrendingUp, TrendingDown, Minus, AlertTriangle, Shield, Target, Zap, BarChart3, PieChart, Globe, Database, FileSearch, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, Outlet, useLocation } from 'react-router-dom';

const API = '';

const SEOOS_TABS = [
  { path: '/admin/seo/audit', label: 'Genel Bakış', exact: true },
  { path: '/admin/seo/audit/technical', label: 'Teknik SEO' },
  { path: '/admin/seo/audit/index-health', label: 'İndeks Sağlığı' },
  { path: '/admin/seo/audit/crawl', label: 'Tarama Analizi' },
  { path: '/admin/seo/audit/content-quality', label: 'İçerik Kalitesi' },
  { path: '/admin/seo/audit/link-graph', label: 'Link Grafiği' },
  { path: '/admin/seo/audit/opportunities', label: 'Fırsatlar' },
  { path: '/admin/seo/audit/war-room', label: 'Savaş Odası' },
  { path: '/admin/seo/audit/reports', label: 'Raporlar' },
  { path: '/admin/seo/audit/fix-center', label: 'AutoFix' },
  { path: '/admin/seo/audit/tasks', label: 'Görevler' },
];

const TAB_TITLES = {
  '/admin/seo/audit': 'Advanced SEO Audit',
  '/admin/seo/audit/overview': 'Advanced SEO Audit',
  '/admin/seo/audit/technical': 'SEO Technical Audit',
  '/admin/seo/audit/index-health': 'SEO Index Health',
  '/admin/seo/audit/crawl': 'SEO Crawl Analysis',
  '/admin/seo/audit/content-quality': 'SEO Content Quality',
  '/admin/seo/audit/link-graph': 'SEO Link Graph',
  '/admin/seo/audit/opportunities': 'SEO Opportunities',
  '/admin/seo/audit/war-room': 'SEO War Room',
  '/admin/seo/audit/reports': 'SEO Reports',
  '/admin/seo/audit/fix-center': 'SEO AutoFix',
  '/admin/seo/audit/tasks': 'SEO Tasks',
};

export function SEOOSLayout() {
  const location = useLocation();

  // Set document.title for SEOOS sub-tabs
  useEffect(() => {
    const title = TAB_TITLES[location.pathname];
    if (title) document.title = `${title} | ODDSIS Admin`;
  }, [location.pathname]);

  return (
    <div>
      <div className="flex items-center gap-1 mb-6 border-b border-purple-500/20 pb-3">
        {SEOOS_TABS.map(tab => {
          const isActive = tab.exact ? location.pathname === tab.path : location.pathname.startsWith(tab.path);
          return (
            <Link key={tab.path} to={tab.path}
              className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                isActive ? 'bg-purple-500/15 text-purple-300 border border-purple-500/30 shadow-[0_0_10px_rgba(147,51,234,0.15)]'
                  : 'text-slate-500 hover:text-purple-300 hover:bg-purple-500/5 border border-transparent'
              }`}>{tab.label}</Link>
          );
        })}
      </div>
      <Outlet />
    </div>
  );
}

// Reusable Score Ring
export function ScoreRing({ score, size = 100, label }) {
  const r = (size - 12) / 2;
  const c = 2 * Math.PI * r;
  const o = c - (score / 100) * c;
  const color = score >= 80 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <div className="relative flex flex-col items-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="6" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="6" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={o} className="transition-all duration-1000" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-black text-white tabular-nums" style={{ fontFamily: 'var(--font-heading)' }}>{score}</span>
        <span className="text-[8px] text-slate-500 uppercase">{label}</span>
      </div>
    </div>
  );
}

// Severity Badge
export function SeverityBadge({ severity }) {
  const labels = { critical: 'Kritik', high: 'Yüksek', medium: 'Orta', low: 'Düşük', safe: 'Güvenli', moderate: 'Orta Risk', info: 'Bilgi' };
  const c = { critical: 'bg-red-500/10 text-red-400 border-red-500/30', high: 'bg-orange-500/10 text-orange-400 border-orange-500/30', medium: 'bg-amber-500/10 text-amber-400 border-amber-500/30', low: 'bg-blue-500/10 text-blue-400 border-blue-500/30', safe: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', moderate: 'bg-amber-500/10 text-amber-400 border-amber-500/30', info: 'bg-violet-500/10 text-violet-400 border-violet-500/30' };
  return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${c[severity] || c.info}`}>{labels[severity] || severity}</span>;
}
